/* eslint-disable no-bitwise,no-plusplus */
import React, { useContext, useState } from 'react';
import useAsyncEffect from 'use-async-effect';
import { useDebouncedCallback } from 'use-debounce';
import { StringParam, useQueryParam } from 'use-query-params';
import { Experience } from '@/webapi/use-experience-api';
import {
  DailyMetrics,
  DailySummaryResponse,
  ExpVersion,
  useAnalyticsApi,
} from '@/webapi/use-analytics-api';
import { MetricKind } from '@/webapi/models';
import { DatePickerContext } from '@/features/details/date-pick/context';
import { Charts } from '@/features/details/charts/charts';
import { NoDateMessage } from '@/features/details/charts/no-data';
import { Opt } from '@/features/details/date-pick/quick-range-select';
import {
  isSmoothOutlierEnabled,
  SummaryContext,
} from '@/features/details/summary/context';
import { CONTROL, DELETED } from '@/features/details/shared';
import { shouldDisplayChartsGraphs } from '@/utils/data_freshness';
import { maybe } from '@/features/details/utils';

type Props = {
  experience: Experience;
  setExperience: (e: Experience) => void;
};

async function chartDataFEtch(
  startDate: Date,
  endDate: Date,
  getDailySummary: (
    experienceId: string,
    goal: MetricKind,
    startAt: Date,
    endAt: Date,
    version?: number,
    expEndDate?: string | undefined,
    st?: string,
    audience?: string,
    activeVariants?: string[],
    isHidden?: boolean,
    outlierSmooth?: boolean,
    isPostPurchase?: boolean,
    customMetrics?: Array<{ name: string; ordinal: number }>,
  ) => Promise<DailySummaryResponse>,
  experience: Experience,
  step: Opt,
  version: ExpVersion,
  isActive: () => boolean,
  setIds: (
    value: ((prevState: Array<string>) => Array<string>) | Array<string>,
  ) => void,
  setData: (value: ((prevState: any[]) => any[]) | any[]) => void,
  st?: string,
  audience?: string,
  activeVariants?: string[],
  outlierSmooth?: boolean,
) {
  if (startDate && endDate) {
    const customMetrics = experience?.elementTracking?.map((x) => ({
      name: x.name,
      ordinal: x.ordinal,
    }));
    const metrics = await getDailySummary(
      experience.id,
      experience.goals?.[0],
      startDate,
      new Date(endDate.getTime()),
      step === Opt.CustomRange ? 0 : version?.version,
      experience?.archivedAt || experience?.endDate,
      st,
      audience,
      activeVariants || [],
      experience.isHidden,
      outlierSmooth,
      experience.isPostPurchase,
      customMetrics,
    );

    if (!isActive()) return;
    metrics?.variant && setIds(Object.keys(metrics?.variant));
    const chartData = transformToChartsData(metrics, experience, version);
    setData(chartData);
  }
}

export function ResultCharts({ experience, setExperience }: Props) {
  const { version, startDate, endDate, step } = useContext(DatePickerContext);
  const { getDailySummary, loading } = useAnalyticsApi();
  const [data, setData] = useState([]);
  const [ids, setIds] = useState<Array<string>>([]);
  const debouncedChartsDataFetch = useDebouncedCallback(chartDataFEtch, 300);
  const [sessionType] = useQueryParam(`st`, StringParam);
  const [audience] = useQueryParam(`aud`, StringParam);
  const activeVariants = Object.keys(
    experience?.versions?.find((v) => v?.version === version?.version)
      ?.variantChances || {},
  );
  const isOutlierSmooth = isSmoothOutlierEnabled(experience);
  useAsyncEffect(
    async (isActive) => {
      await debouncedChartsDataFetch(
        startDate,
        endDate,
        getDailySummary,
        experience,
        step,
        version,
        isActive,
        setIds,
        setData,
        sessionType,
        audience,
        activeVariants,
        isOutlierSmooth,
      );
    },
    [
      startDate?.toLocaleDateString(),
      endDate?.toLocaleDateString(),
      version?.version,
      sessionType,
      audience,
      isOutlierSmooth,
    ],
  );

  const { variants, data: summaryResponse } = useContext(SummaryContext);

  const legend = variants
    .filter((v) => ids.includes(v.id))
    ?.map((v) => v.name || DELETED);

  const showChart = shouldDisplayChartsGraphs(data, experience, version);

  if (showChart) {
    return (
      <Charts
        setExperience={setExperience}
        experience={experience}
        variations={legend}
        loading={loading}
        data={data}
      />
    );
  }

  return (
    <NoDateMessage
      loading={loading}
      hasDataForAllAudiences={summaryResponse?.hasDataForAllAudiences}
    />
  );
}

function toChartsFormat(dailyMetrics: DailyMetrics) {
  const out = { date: dailyMetrics.day.replaceAll(`T00:00:00Z`, ``) };
  dailyMetrics.metrics.forEach((m) => {
    if (
      m.name === MetricKind.CONVERSION_RATE ||
      m.name === MetricKind.RETURNS_PER_SESSION ||
      m.name === MetricKind.SUBSCRIPTION_RATE
    ) {
      out[m.name] = Math.round((m.value * 100 || 0) * 1e6) / 1e6;
    } else {
      out[m.name] = Math.round((m.value || 0) * 1e6) / 1e6;
    }
  });
  return out;
}

const colors = [
  `#622CF6`,
  `#F6A1CD`,
  `#9BCDF6`,
  `#D3B6F6`,
  `#AEEEB7`,
  `#F6EAA5`,
  `#FF7F00`,
  `#00CED1`,
  `#FF1493`,
  `#008000`,
  `#FFD700`,
  `#800080`,
  `#FF4500`,
  `#00FF00`,
  `#4169E1`,
  `#DC143C`,
  `#00FFFF`,
  `#FFC0CB`,
];
const nameColorMap = new Map<string, string>();
let colorIdx = 0;
export function getSeriesColor(name: string): string {
  if (name === CONTROL) {
    return `#0BCDAE`;
  }
  if (!name || name === DELETED) {
    return `#d3d3d3`;
  }
  const color = nameColorMap.get(name);
  if (color) {
    return color;
  }

  nameColorMap.set(name, colors[colorIdx % 18]);
  colorIdx += 1;
  return nameColorMap.get(name);
}

export function transformToChartsData(
  metrics: DailySummaryResponse,
  experience: Experience,
  version?: ExpVersion,
) {
  const controlId = experience?.variants?.find((v) => v.name === CONTROL)?.id;
  const variantId = experience?.variants?.find((v) => v.name !== CONTROL)?.id;
  const hasSessions = (m) =>
    m.metrics.find((m) => m.name === MetricKind.SESSIONS).value > 0;
  const controlDays = metrics?.variant?.[controlId]?.metrics
    ?.filter(hasSessions)
    ?.map((m) => m.day);
  const variantDays = metrics?.variant?.[variantId]?.metrics
    ?.filter(hasSessions)
    ?.map((m) => m.day);
  const missingDaysInVariant =
    controlDays?.filter((day) => !variantDays?.includes(day)) || [];
  const missingInControl =
    variantDays?.filter((day) => !controlDays?.includes(day)) || [];
  const variantMetrics =
    metrics?.variant?.[variantId]?.metrics?.filter(hasSessions);
  const controlMetrics =
    metrics?.variant?.[controlId]?.metrics?.filter(hasSessions);

  variantMetrics?.forEach(({ metrics: m, day }) => {
    if (missingInControl?.includes(day)) {
      const zero = m?.map((x) => {
        const ret = {} as any;
        ret.name = x?.name;
        ret.value = ret.name === MetricKind.SESSIONS ? 1 : 0;
        return ret;
      });
      metrics?.variant?.[controlId]?.metrics?.push({ day, metrics: zero });
    }
  });

  controlMetrics?.forEach(({ metrics: m, day }) => {
    if (missingDaysInVariant?.includes(day)) {
      const zero = m?.map((x) => {
        const ret = {} as any;
        ret.name = x.name;
        ret.value = ret.name === MetricKind.SESSIONS ? 1 : 0;
        return ret;
      });
      metrics?.variant?.[variantId]?.metrics?.push({ day, metrics: zero });
    }
  });

  const all100Variant = experience?.variants?.find(
    (v) => v.publishedChance === 100,
  );
  const shouldHideControl =
    !!all100Variant && all100Variant.name !== CONTROL && version?.isCurrent;
  if (shouldHideControl) {
    delete metrics?.variant?.[controlId];
  }
  const result = Object.entries(metrics?.variant ?? {})?.map((entry) => {
    const [variantId, value] = entry;
    const name = experience.variants?.filter((v) => v.id === variantId)?.[0]
      ?.name;
    const metricData = value.metrics
      .filter(hasSessions)
      .sort((a, b) => new Date(a.day).getTime() - new Date(b.day).getTime())
      .map(toChartsFormat);

    return {
      name,
      data: metricData,
    };
  });
  maybe(() => padWithZeroDays(result));
  return result;
}
const padWithZeroDays = (map: { data: { date: string }[]; name: string }[]) => {
  createConsecutiveDates(map).forEach((day) => {
    map.forEach((value) => {
      if (!value.data.find((x) => x.date === day)) {
        value.data.push(zeroDay(day));
        value.data.sort(
          (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime(),
        );
      }
    });
  });
};
const zeroDay = (day) =>
  ({
    date: day,
    ADD_TO_CART: 0,
    REVENUE: 0,
    TRANSACTIONS: 0,
    AVG_ORDER_VALUE: 0,
    SESSIONS: 0,
    PER_SESSION_VALUE: 0,
    CONVERSION_RATE: 0,
    PAGE_VIEWS: 0,
    ITEMS_PER_PURCHASE: 0,
    PRODUCT_DETAIL_VIEWS: 0,
    PAGES_PER_SESSION: 0,
    AVG_SESSION_DURATION: 0,
    REMOVE_FROM_CART: 0,
    ASSISTED_REVENUE: 0,
    DIRECT_REVENUE: 0,
    CTR: 0,
    CLICKS: 0,
    ADD_TO_CART_RATE: 0,
    PRODUCT_DETAILS_VIEWS_RATE: 0,
    REVENUE_FROM_CLICKS: 0,
    SIGNUPS: 0,
    SIGNUPS_RATE: 0,
    SUBSCRIPTIONS: 0,
    SUBSCRIPTIONS_RATE: 0,
    PROFIT: 0,
    PROFIT_PER_SESSION: 0,
    RETURNS: 0,
    RETURNS_PER_SESSION: 0,
    CHECKOUT_RATE: 0,
  } as any);
const createConsecutiveDates = (
  map: { data: { date: string }[]; name: string }[],
) => {
  const combinedArray = map.flatMap((c) => c.data.map((d) => d.date) as any);
  const parsedDates = combinedArray.map((date) => new Date(date).valueOf());
  const minDate = new Date(Math.min(...parsedDates));
  const maxDate = new Date(Math.max(...parsedDates));

  const consecutiveDates = [];
  for (
    let date = new Date(minDate);
    date <= maxDate;
    date.setDate(date.getDate() + 1)
  ) {
    consecutiveDates.push(new Date(date).toISOString().split(`T`)[0]);
  }
  return consecutiveDates;
};
